import { A, useNavigate } from "@solidjs/router";
import { useQuery } from "@tanstack/solid-query";
import {
  type YeetAgentPromptDocument,
  YeetAgentPromptDocumentSchema,
} from "contracts/prompts/yeet-agent-document";
import { createSignal, For, Show } from "solid-js";
import { createStore, produce, unwrap } from "solid-js/store";
import { adminGateProps } from "../../store/admin";
import { api } from "../../store/api";
import { createAsyncAction } from "../../ui/asyncAction";
import { invalidatePromptBundles, promptEditorKey } from "./playgroundQueries";
import { TEXT_FRAGMENTS } from "./promptFragments";

/** Edit an AI-instruction prompt. Prompts are immutable, so saving an existing
 * one forks a new version; a brand-new prompt is seeded from the current
 * default so the editor always starts from working text. */
export function PromptBundleEditor(props: { bundleId?: string }) {
  const source = useQuery(() => ({
    queryKey: promptEditorKey(props.bundleId),
    queryFn: () =>
      props.bundleId
        ? api.prompts.getBundle(props.bundleId)
        : api.prompts.activeBundle(),
    staleTime: Number.POSITIVE_INFINITY,
  }));

  return (
    <main class="landing">
      <div class="landing-bar">
        <A href="/playground/prompts" class="landing-brand">
          ← Prompt library
        </A>
      </div>
      <div class="landing-main">
        <Show
          when={source.data}
          fallback={<div class="loading">Loading prompt…</div>}
        >
          {(data) => (
            <EditorBody
              editing={props.bundleId !== undefined}
              parentId={props.bundleId}
              initialLabel={
                props.bundleId ? `${data().label} (copy)` : "New prompt"
              }
              // This editor only handles the yeet-agent surface; parsing
              // rejects any other surface's bundle loudly.
              initialDocument={YeetAgentPromptDocumentSchema.parse(
                data().document,
              )}
            />
          )}
        </Show>
      </div>
    </main>
  );
}

function EditorBody(props: {
  editing: boolean;
  parentId?: string;
  initialLabel: string;
  initialDocument: YeetAgentPromptDocument;
}) {
  const navigate = useNavigate();
  const [label, setLabel] = createSignal(props.initialLabel);
  const [doc, setDoc] = createStore<YeetAgentPromptDocument>({
    ...props.initialDocument,
    steps: [...props.initialDocument.steps],
  });
  const [makeDefault, setMakeDefault] = createSignal(false);
  const action = createAsyncAction();

  const save = () => {
    if (!label().trim() || action.busy()) return;
    void action.run(async () => {
      const input = { label: label().trim(), document: unwrap(doc) };
      const saved =
        props.editing && props.parentId
          ? await api.prompts.forkBundle(props.parentId, input)
          : await api.prompts.createBundle(input);
      if (makeDefault()) await api.prompts.activateBundle(saved.id);
      await invalidatePromptBundles();
      navigate("/playground/prompts");
    });
  };

  return (
    <>
      <div class="landing-head-row">
        <div>
          <h1 class="landing-h1">
            {props.editing ? "Edit prompt" : "New prompt"}
          </h1>
          <p class="landing-sub pg-lede">
            Reword any section below. Saving keeps the original and creates a
            new version you can make the default.
          </p>
          <Show when={action.error()}>
            <p class="badge-sev sev-critical">{action.error()}</p>
          </Show>
        </div>
        <div class="param-bar-actions">
          <label class="radio-row">
            <input
              type="checkbox"
              checked={makeDefault()}
              onChange={(e) => setMakeDefault(e.currentTarget.checked)}
            />
            Make default
          </label>
          <button
            class="btn btn-primary"
            type="button"
            {...adminGateProps(action.busy() || !label().trim(), "save")}
            onClick={save}
          >
            {action.busy() ? "Saving…" : "Save prompt"}
          </button>
        </div>
      </div>

      <div class="panel">
        <div class="panel-body">
          <label class="field">
            <span class="field-label">Name</span>
            <input
              class="editor-input"
              value={label()}
              onInput={(e) => setLabel(e.currentTarget.value)}
            />
          </label>

          <For each={TEXT_FRAGMENTS}>
            {(fragment) => (
              <div class="prompt-fragment">
                <span class="field-label">{fragment.label}</span>
                <span class="field-hint">{fragment.hint}</span>
                <textarea
                  class="editor-input"
                  value={doc[fragment.key]}
                  onInput={(e) => setDoc(fragment.key, e.currentTarget.value)}
                />
              </div>
            )}
          </For>

          <div class="prompt-fragment">
            <span class="field-label">Investigation steps</span>
            <span class="field-hint">
              The ordered checklist the agent must work through.
            </span>
            <div class="fragment-steps">
              <For each={doc.steps}>
                {(step, index) => (
                  <div class="fragment-step-row">
                    <span class="flow-step-num">{index() + 1}</span>
                    <textarea
                      class="editor-input"
                      value={step}
                      onInput={(e) =>
                        setDoc("steps", index(), e.currentTarget.value)
                      }
                    />
                    <button
                      class="btn"
                      type="button"
                      onClick={() =>
                        setDoc(
                          "steps",
                          produce((steps) => {
                            steps.splice(index(), 1);
                          }),
                        )
                      }
                    >
                      Remove
                    </button>
                  </div>
                )}
              </For>
              <button
                class="btn"
                type="button"
                onClick={() =>
                  setDoc(
                    "steps",
                    produce((steps) => {
                      steps.push("");
                    }),
                  )
                }
              >
                Add step
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
